import { useEffect, useRef, useState } from 'react';
import { APP_NAME } from 'shared';
import type { Session } from './api';
import { logout } from './api';
import { LANG_LABEL, LANGS, useT } from './i18n';
import { useTheme, type ThemeMode } from './theme';
import Icon from './Icon';
import Notifications from './Notifications';
import Pulse from './Pulse';
import { isTelegram } from './telegram';

const THEME_ICON: Record<ThemeMode, 'sun' | 'moon' | 'auto'> = {
  light: 'sun',
  dark: 'moon',
  system: 'auto',
};

/** Инициалы вместо картинки: аватары мы не храним, а пустой круг — это дыра. */
function initials(u: Session['user']): string {
  const src =
    [u.firstName, u.lastName].filter(Boolean).join(' ') || u.login || '?';
  return src
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function roleLabel(u: Session['user'], t: (s: string) => string): string {
  if (u.isAdmin) return t('Администратор');
  if (u.role === 'LOGIST') return t('Логист');
  if (u.role === 'DRIVER') return t('Перевозчик');
  return `${t('Логист')} · ${t('Перевозчик')}`;
}

interface Props {
  user: Session['user'];
  onProfile: () => void;
  onOpenCargo: (id: string) => void;
  onOpenOrders: () => void;
}

/**
 * Верхняя панель: кто вошёл, тема, язык.
 *
 * Живёт на всех экранах, включая Mini App. В Telegram шапка уже есть, но
 * своя нужна: переключатели темы и языка должны быть под рукой, а не
 * закопаны в профиле.
 */
export default function TopBar({
  user,
  onProfile,
  onOpenCargo,
  onOpenOrders,
}: Props) {
  const { mode, setMode, cycle } = useTheme();
  const { lang, t, setLang, next } = useT();
  const [menu, setMenu] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // Клик мимо меню закрывает его: без этого на вебе оно висит,
  // пока не нажмёшь ту же кнопку.
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menu]);

  const themeTitle = `${t('Тема')}: ${t(
    mode === 'light' ? 'Светлая' : mode === 'dark' ? 'Тёмная' : 'Системная',
  )}`;

  return (
    <div className="topbar" ref={box}>
      <div className="topbar__brand">
        <span className="topbar__logo" aria-hidden>
          <Icon name="truck" size={18} />
        </span>
        <span>
          <span className="topbar__name">{APP_NAME}</span>
          <span className="topbar__sub">{t('Биржа грузоперевозок')}</span>
        </span>
      </div>

      <Pulse />

      <Notifications onOpenCargo={onOpenCargo} onOpenOrders={onOpenOrders} />

      <button
        className="icon-btn"
        onClick={cycle}
        title={themeTitle}
        aria-label={themeTitle}
      >
        <Icon name={THEME_ICON[mode]} size={17} />
      </button>

      <button
        className="icon-btn"
        onClick={() => setLang(next())}
        title={`${t('Язык')}: ${LANG_LABEL[lang]}`}
        aria-label={t('Язык')}
      >
        {LANG_LABEL[lang]}
      </button>

      <button className="who" onClick={() => setMenu((v) => !v)}>
        <span className="avatar" aria-hidden>
          {initials(user)}
        </span>
        <span className="who__text">
          <span className="who__name">
            {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
              user.login ||
              '—'}
          </span>
          <span className="who__role">{roleLabel(user, t)}</span>
        </span>
      </button>

      {menu && (
        <div className="menu" role="menu">
          <div className="menu__head">
            <div style={{ fontWeight: 600 }}>
              {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                user.login}
            </div>
            <div className="meta">{roleLabel(user, t)}</div>
          </div>

          <button
            onClick={() => {
              setMenu(false);
              onProfile();
            }}
          >
            <Icon name="user" size={17} />
            {t('Профиль')}
          </button>

          <div className="divider" style={{ margin: '4px 0' }} />

          {/* Тема и язык — взаимоисключающий выбор одного варианта, а не
              переключаемые фильтры: по правилу проекта это `.seg`, не
              `.chips` (тот же случай, что и с разделами админки). */}
          <div className="meta" style={{ padding: '4px 8px' }}>
            {t('Тема')}
          </div>
          <div className="seg" style={{ margin: '0 8px 8px' }}>
            {(['system', 'light', 'dark'] as ThemeMode[]).map((m) => (
              <button
                key={m}
                type="button"
                className={mode === m ? 'seg__btn seg__btn--on' : 'seg__btn'}
                onClick={() => setMode(m)}
              >
                {t(m === 'light' ? 'Светлая' : m === 'dark' ? 'Тёмная' : 'Системная')}
              </button>
            ))}
          </div>

          <div className="meta" style={{ padding: '4px 8px' }}>
            {t('Язык')}
          </div>
          <div className="seg" style={{ margin: '0 8px 8px' }}>
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                className={lang === l ? 'seg__btn seg__btn--on' : 'seg__btn'}
                onClick={() => setLang(l)}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>

          {/* В Telegram выхода нет: аккаунт там определяется мессенджером. */}
          {!isTelegram && (
            <>
              <div className="divider" style={{ margin: '4px 0' }} />
              <button
                onClick={() => {
                  logout();
                  window.location.reload();
                }}
              >
                <Icon name="logout" size={17} />
                {t('Выйти')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
